import { findCartPairingBundle } from '@hungryroot/cart';
import { usePairingInventoryParams } from '@hungryroot/inventory';
import { useNextOrderV3 } from '@hungryroot/orders';
import { usePairing } from '@hungryroot/rest';
import { useCustomization } from '../../../providers/CustomizationProvider/CustomizationProvider';
import { type Props as PairingEditProps } from '../../edit-recipe/PairingEdit/PairingEdit';
import { RDPModal } from '../../RDP/RDPModal/RDPModal';

type Props = {
  onDismiss: () => void;
  pairingId: number;
  index?: number | null;
  onProductContentScroll?: (scrollDepth: number) => void;
};

export function EditableFavoritableRdpModalFromPairingId(props: Props) {
  // the index here is for salad shooter tracking purposes; however, this has not been implemented
  // every place, so it's set as null by default to signal to the data team the data sent is not
  // part of the for you experience
  const { onDismiss, pairingId, index = null, onProductContentScroll } = props;

  const outOfStockOptions = usePairingInventoryParams();

  const { data: pairingData, isLoading: pairingLoading } = usePairing({
    id: pairingId,
    ...outOfStockOptions,
  });

  const { order, isLoading: orderLoading } = useNextOrderV3();
  const customization = useCustomization();
  const cart = 'cart' in customization ? customization.cart : undefined;
  const bundle = pairingId && cart && findCartPairingBundle(cart, pairingId);

  if (pairingLoading || orderLoading || pairingData === undefined) {
    return null;
  }

  const pairing: PairingEditProps['initialPairing'] = {
    ...pairingData,
    points: parseFloat(pairingData.points),
    featured_img: pairingData.featured_img_url,
    nutrition_html: pairingData.nutrition_info,
    is_favorited: false, // TODO: MB - https://hungryroot.atlassian.net/browse/WEB-828
    dish_type:
      pairingData.dish_type !== null
        ? {
            ...pairingData.dish_type,
            sort_order: 0,
          }
        : null,
    ingredients: pairingData.ingredients.map((ingredient) => {
      return {
        ...ingredient,
        // context to make this backwards compatible https://hungryroot.slack.com/archives/C050U424A7K/p1686925483506159
        amount:
          pairingData.needed_ingredients.find(
            (i) => (i.id || i.ingredient_id) === ingredient.id
          )?.amount ?? 0,
        points: parseFloat(ingredient.points),
        card_image_url:
          ingredient.card_image_url === null ? '' : ingredient.card_image_url,
      };
    }),
    tags: pairingData.tags.map((tag) => tag.id),
  };

  return (
    <RDPModal
      index={index}
      onDismiss={onDismiss}
      orderId={order?.id}
      initialBundle={
        bundle
          ? {
              order_details: bundle.order_details,
              points: bundle.points,
            }
          : undefined
      }
      initialPairing={pairing}
      onProductContentScroll={onProductContentScroll}
    />
  );
}
